import { Link } from "react-router-dom";
import { DONATE_URL } from "@/lib/links";
import { motion } from "framer-motion";
import {
  Shield, ShieldCheck, Users, HeartHandshake, Flag, ArrowRight, Target,
  Radio, Star, CheckCircle2, ChevronDown, Crosshair, Scroll, Landmark,
} from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { NewsletterSignup } from "@/components/NewsletterSignup";
import { Testimonials } from "@/components/Testimonials";
import { ChapterSection } from "@/components/ChapterSection";
import { CountUp } from "@/components/CountUp";

const HERO_FLAG = "https://images.unsplash.com/photo-1631906175843-6f7d59f7d485?crop=entropy&cs=srgb&fm=jpg&q=85";
const HERO_DEFENDERS = "https://customer-assets-39nsmqrw.emergentagent.net/job_defense-corps-1/artifacts/coxj0z0f_Screenshot%202026-10-01%20125818.png";
// Saved for later re-upload — hero leadership photo (currently hidden):
const HERO_CEO = "https://customer-assets-39nsmqrw.emergentagent.net/job_defense-corps-1/artifacts/oy2t9kdr_David%20A.%20Walton%20CEO.png";
const EAGLE = "https://images.unsplash.com/photo-1628703275211-675419b0b995?crop=entropy&cs=srgb&fm=jpg&q=85";
const VETS = "https://images.unsplash.com/photo-1609692235164-394d6828466c?crop=entropy&cs=srgb&fm=jpg&q=85";
const CONSTITUTION = "https://images.unsplash.com/photo-1515040242872-08257d6d08c2?crop=entropy&cs=srgb&fm=jpg&q=85";
const CAPITOL = "https://images.unsplash.com/photo-1594581979864-36977b15d0dc?crop=entropy&cs=srgb&fm=jpg&q=85";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.12 } }),
};

const pillars = [
  { icon: Shield, title: "Community Defense", text: "Trained, organized, armed and ready. We prepare citizens to protect their neighborhoods and stand shoulder-to-shoulder with each other and first responders." },
  { icon: Users, title: "Strength in Numbers", text: "A nationwide network of patriots united by duty. Local chapters, national coordination, and real impact on the ground where it counts." },
  { icon: HeartHandshake, title: "Service First", text: "Armed response, business protection, veteran support, house-of-worship and school security, and civic action. We show up when it matters most — and we never back down." },
];

const readiness = [
  "Vetted, trained volunteer defenders in every region",
  "24/7 coordinated rapid-response network",
  "Faith, family, and freedom at the core of the mission",
];

const principles = [
  { icon: Scroll, title: "Defend The Constitution", text: "We stand on the supreme law of the land and the God-given rights it secures for every citizen." },
  { icon: Shield, title: "Protect The 2nd Amendment", text: "A free people hold the right to keep and bear arms in defense of home, family, and nation." },
  { icon: Flag, title: "Faith, Family & Freedom", text: "The founding principles that built America guide every decision this movement makes." },
  { icon: Landmark, title: "Secure Our Communities", text: "Organized local chapters ready to protect schools, houses of worship, and neighborhoods." },
];

const BILL_OF_RIGHTS = [
  ["I", "Freedom of Speech, Religion & Press"],
  ["II", "Right to Keep & Bear Arms"],
  ["III", "No Quartering of Soldiers"],
  ["IV", "Protection from Unreasonable Search"],
  ["V", "Right to Due Process of Law"],
  ["VI", "Right to a Fair & Speedy Trial"],
  ["VII", "Right to Trial by Jury"],
  ["VIII", "No Cruel & Unusual Punishment"],
  ["IX", "Rights Retained by the People"],
  ["X", "Powers Reserved to the States"],
];

export default function Landing() {
  return (
    <div>
      <Navbar />

      {/* HERO */}
      <section className="relative min-h-screen flex items-center grain stars tactical-grid overflow-hidden bg-[#0A1128]">
        <img src={HERO_FLAG} alt="American flag" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A1128] via-[#0A1128]/92 to-[#0A1128]/55" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128] via-[#0A1128]/35 to-[#0A1128]/60" />
        <div className="absolute top-1/4 -left-40 w-[620px] h-[620px] glow-red pointer-events-none" />

        {/* tactical side marker */}
        <span className="v-label hidden xl:block absolute left-6 top-1/2 -translate-y-1/2 text-[10px] tracking-[0.4em] uppercase text-white/30 font-semibold">
          Non-Partisan · Citizen-Led · Est. USA
        </span>

        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full pt-28 pb-16 grid lg:grid-cols-12 gap-10 items-center">
          {/* Left: message */}
          <div className="lg:col-span-7">
            <motion.div initial="hidden" animate="show" variants={fadeUp} custom={0}>
              <span className="kicker-pill mb-6">
                <Radio className="w-3.5 h-3.5" /> Nationwide Citizen Defense Movement
              </span>
            </motion.div>
            <motion.h1
              initial="hidden" animate="show" variants={fadeUp} custom={1}
              className="font-display text-5xl sm:text-7xl lg:text-[5.5rem] text-white uppercase leading-[0.88] max-w-4xl drop-shadow-[0_2px_20px_rgba(0,0,0,0.6)]"
            >
              The Power of Patriots.<br />
              <span className="text-[#D90429]">The Strength</span> Of America.
            </motion.h1>
            <motion.p
              initial="hidden" animate="show" variants={fadeUp} custom={2}
              className="text-white/75 text-base sm:text-lg max-w-2xl mt-8 leading-relaxed"
            >
              Patriot Power USA is a nationwide patriot organization of military combat veterans,
              former law enforcement, and everyday American patriots committed to the protection and
              defense of our families, our communities, and our nation. We are trained, organized,
              united, and ready to protect your freedom — we stand ready to serve.
            </motion.p>
            <motion.div
              initial="hidden" animate="show" variants={fadeUp} custom={3}
              className="flex flex-col sm:flex-row gap-4 mt-10"
            >
              <Link
                to="/join"
                data-testid="hero-join-button"
                className="group inline-flex items-center justify-center gap-2 bg-white text-[#0A1128] px-8 py-4 rounded-full uppercase tracking-[0.15em] text-sm font-bold hover:bg-[#F4A261] transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0 shadow-lg"
              >
                Join Our Newsletter <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
              </Link>
              <a
                href={DONATE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-donate-button"
                className="btn-glow inline-flex items-center justify-center gap-2 bg-[#D90429] text-white px-8 py-4 rounded-full uppercase tracking-[0.15em] text-sm font-bold hover:bg-[#EF233C]"
              >
                Donate Now <ArrowRight className="w-4 h-4" />
              </a>
            </motion.div>
            <motion.div
              initial="hidden" animate="show" variants={fadeUp} custom={4}
              className="flex flex-wrap items-center gap-x-7 gap-y-3 mt-12 text-white/70"
            >
              {[[ShieldCheck, "Veteran-Led"], [Flag, "Faith & Freedom"], [Users, "50-State Network"], [Star, "100% Volunteer Backed"]].map(([Ic, label]) => (
                <span key={label} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] font-semibold">
                  <Ic className="w-4 h-4 text-[#F4A261]" /> {label}
                </span>
              ))}
            </motion.div>
          </div>

          {/* Right: defenders visual with tactical framing */}
          <motion.div
            initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="lg:col-span-5 hidden lg:block relative"
          >
            <div className="notch relative overflow-hidden border border-white/15">
              <img src={HERO_DEFENDERS} alt="Patriot Power USA leadership" className="w-full h-[520px] object-cover object-top" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128] via-transparent to-transparent" />
              <div className="absolute top-0 left-0 w-1.5 h-full bg-[#D90429]" />
              {/* floating tactical status card */}
              <div className="absolute bottom-5 left-5 right-5 bg-[#0A1128]/85 backdrop-blur-md border border-[#F4A261]/30 px-5 py-4 flex items-center gap-4">
                <div className="w-11 h-11 flex items-center justify-center bg-[#D90429]/20 border border-[#D90429]/40 shrink-0">
                  <Crosshair className="w-6 h-6 text-[#D90429]" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#F4A261] font-semibold">Operational Status</div>
                  <div className="font-display text-xl text-white uppercase leading-tight">Ready · Vigilant · United</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* scroll cue */}
        <a href="#mission" className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 hidden sm:flex flex-col items-center gap-1 text-white/50 hover:text-white transition-colors">
          <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
          <ChevronDown className="w-5 h-5 scroll-cue" />
        </a>
        <div className="absolute bottom-0 left-0 right-0 h-1.5 chevron-accent opacity-80" />
      </section>

      {/* STAT BAR */}
      <section className="bg-[#D90429] text-white relative overflow-hidden">
        <div className="rule-redgold" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 grid grid-cols-2 md:grid-cols-4 gap-y-8 divide-x divide-white/15">
          {[["50", "States Covered"], ["12K+", "Active Members"], ["400+", "Local Chapters"], ["24/7", "Ready to Respond"]].map(([n, l], i) => (
            <motion.div
              key={l} data-testid="stat-item"
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="text-center px-4"
            >
              <div className="font-display text-5xl sm:text-6xl leading-none drop-shadow-[0_2px_8px_rgba(0,0,0,0.25)]"><CountUp value={n} /></div>
              <div className="text-xs uppercase tracking-[0.2em] text-white/85 mt-2">{l}</div>
            </motion.div>
          ))}
        </div>
        <div className="chevron-accent h-1 opacity-70" />
      </section>

      {/* MISSION */}
      <section id="mission" className="py-24 sm:py-32 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-16 items-center">
          {/* image collage */}
          <motion.div
            initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
            className="relative"
          >
            <div className="notch relative">
              <img src={EAGLE} alt="Bald eagle" className="w-full h-[500px] object-cover" />
              <div className="absolute top-0 left-0 w-1.5 h-full bg-[#D90429]" />
            </div>
            <div className="absolute -bottom-8 -right-4 sm:-right-8 w-44 sm:w-56 border-4 border-white shadow-xl hidden sm:block">
              <img src={VETS} alt="Veterans standing together" className="w-full h-40 sm:h-48 object-cover" />
            </div>
            <div className="absolute -top-5 -left-5 bg-[#0A1128] text-white px-5 py-4 hidden sm:flex items-center gap-3 shadow-xl">
              <Flag className="w-7 h-7 text-[#D90429]" />
              <span className="font-display text-lg uppercase leading-tight">Duty.<br />Honor.<br />Country.</span>
            </div>
          </motion.div>

          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}>
            <span className="kicker-pill">Our Mission</span>
            <h2 className="font-display text-4xl sm:text-6xl uppercase mt-4 leading-[0.92]">
              We Don't Wait.<br /><span className="text-[#D90429]">We Prepare.</span>
            </h2>
            <p className="text-lg text-slate-600 mt-6 leading-relaxed">
              Patriot Power USA exists to build resilient communities capable of protecting
              themselves. A free nation is defended not only by its armed forces, but by an
              informed, trained, and united citizenry.
            </p>
            <ul className="mt-8 space-y-3">
              {readiness.map((r) => (
                <li key={r} className="flex items-start gap-3 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-[#D90429] shrink-0 mt-0.5" />
                  <span className="font-medium">{r}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/join"
              data-testid="mission-join-button"
              className="inline-flex items-center gap-2 mt-10 text-[#0A1128] font-bold uppercase tracking-[0.15em] text-sm border-b-2 border-[#D90429] pb-1 hover:gap-4 transition-all duration-200"
            >
              Join Our Newsletter <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CONSTITUTION PLATFORM */}
      <section className="bg-[#0A1128] text-white py-24 sm:py-32 grain stars relative overflow-hidden">
        <div className="absolute -right-40 top-1/3 w-[560px] h-[560px] glow-gold pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-16 items-center relative z-10">
          {/* We The People image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative order-2 lg:order-1"
          >
            <div className="notch relative overflow-hidden border border-white/15">
              <img src={CONSTITUTION} alt="We The People — United States Constitution" className="w-full h-[500px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128] via-[#0A1128]/20 to-transparent" />
              <div className="absolute top-0 left-0 w-1.5 h-full bg-[#D90429]" />
            </div>
            <div className="absolute -bottom-6 left-6 right-6 sm:left-10 sm:right-10 bg-[#0A1128]/90 backdrop-blur-md border border-[#F4A261]/30 px-6 py-4 text-center">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#F4A261] font-semibold">The Supreme Law Of The Land</div>
              <div className="font-display text-lg sm:text-xl uppercase mt-1">Ratified 1788 · Defended Today</div>
            </div>
          </motion.div>

          {/* Principles / platform */}
          <motion.div
            initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
            className="order-1 lg:order-2"
          >
            <span className="kicker-pill">Where We Stand</span>
            <h2 className="font-display text-4xl sm:text-6xl uppercase mt-4 leading-[0.92]">
              We Stand On The<br /><span className="text-[#D90429]">Constitution</span>
            </h2>
            <p className="text-white/70 text-lg mt-6 leading-relaxed max-w-xl">
              Patriot Power USA is built on the founding documents of this nation. Our platform
              is not partisan — it is patriotic. These are the principles we defend.
            </p>
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-8 mt-10">
              {principles.map((p) => (
                <div key={p.title} className="flex gap-4">
                  <div className="w-11 h-11 shrink-0 flex items-center justify-center bg-[#D90429]/15 border border-[#D90429]/30">
                    <p.icon className="w-5 h-5 text-[#F4A261]" strokeWidth={1.75} />
                  </div>
                  <div>
                    <h3 className="font-display text-lg uppercase leading-tight">{p.title}</h3>
                    <p className="text-white/55 text-sm mt-1.5 leading-relaxed">{p.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Bill of Rights scrolling strip */}
        <div className="relative z-10 mt-16">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 mb-4">
            <span className="text-[11px] uppercase tracking-[0.3em] text-white/45 font-semibold">The Bill Of Rights · The First Ten Amendments</span>
          </div>
          <div data-testid="bill-of-rights-strip" className="border-y border-white/10 bg-black/20 py-4 overflow-hidden ticker-mask">
            <div className="flex items-center gap-10 whitespace-nowrap animate-ticker w-max">
              {[...BILL_OF_RIGHTS, ...BILL_OF_RIGHTS].map(([n, t], i) => (
                <span key={i} className="inline-flex items-center gap-3 text-sm uppercase tracking-[0.15em] text-white/70">
                  <span className="font-display text-[#F4A261] text-lg">{n}</span>
                  <span>{t}</span>
                  <Star className="w-3.5 h-3.5 text-[#D90429] fill-[#D90429]" />
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="bg-[#0A1128] text-white py-24 sm:py-32 grain stars tactical-grid relative">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 relative z-10">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-16">
            <div>
              <span className="kicker-pill mb-4">Our Creed</span>
              <h2 className="font-display text-4xl sm:text-6xl uppercase max-w-xl leading-[0.92] mt-2">
                What We <span className="text-[#D90429]">Stand</span> For
              </h2>
            </div>
            <p className="text-white/60 max-w-sm">Three principles that guide every chapter, every mission, and every member of the movement.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {pillars.map((p, i) => (
              <motion.div
                key={p.title}
                initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i}
                data-testid="pillar-card"
                className="notch bg-[#111936]/70 border border-white/10 p-8 hover:border-[#D90429]/60 hover:bg-[#111936] transition-all duration-200 group hover:-translate-y-1.5"
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="w-14 h-14 flex items-center justify-center bg-[#D90429]/15 border border-[#D90429]/30 group-hover:bg-[#D90429] transition-colors duration-200">
                    <p.icon className="w-7 h-7 text-[#D90429] group-hover:text-white transition-colors duration-200" strokeWidth={1.75} />
                  </div>
                  <span className="font-display text-4xl text-white/10 group-hover:text-[#F4A261]/30 transition-colors duration-200">
                    0{i + 1}
                  </span>
                </div>
                <h3 className="font-display text-2xl uppercase mb-3">{p.title}</h3>
                <p className="text-white/60 leading-relaxed">{p.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PREAMBLE QUOTE BAND */}
      <section className="relative py-28 sm:py-36 grain overflow-hidden bg-[#0A1128]">
        <img src={CAPITOL} alt="United States Capitol" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A1128] via-[#0A1128]/80 to-[#0A1128]" />
        <div className="rule-redgold absolute top-0 left-0 right-0" />
        <motion.div
          initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="relative z-10 max-w-4xl mx-auto px-5 sm:px-8 text-center"
        >
          <Scroll className="w-9 h-9 text-[#F4A261] mx-auto mb-8" strokeWidth={1.5} />
          <p className="font-display text-2xl sm:text-4xl lg:text-5xl text-white uppercase leading-[1.05]">
            "We the People, in Order to form a more perfect Union,
            <span className="text-[#D90429]"> establish Justice</span>, insure domestic Tranquility,
            provide for the common defence…"
          </p>
          <p className="text-[#F4A261] text-xs uppercase tracking-[0.3em] font-semibold mt-8">
            — Preamble, The Constitution of the United States
          </p>
        </motion.div>
      </section>

      {/* TESTIMONIALS */}
      <Testimonials />

      {/* JOIN CTA SPLIT */}
      <section className="grid lg:grid-cols-2">
        <div className="relative min-h-[460px]">
          <img src={VETS} alt="Patriots in uniform standing together" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1128]/60 via-[#0A1128]/20 to-[#0A1128]/40" />
          <div className="absolute top-0 left-0 h-1.5 w-full chevron-accent opacity-80" />
        </div>
        <div className="bg-[#0A1128] text-white flex items-center py-20 px-8 sm:px-16 grain relative">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="relative z-10">
            <Target className="w-10 h-10 text-[#D90429] mb-6" />
            <span className="kicker-pill mb-4">Enlist Today</span>
            <h2 className="font-display text-4xl sm:text-5xl uppercase leading-[0.92] mt-2">
              Answer The Call
            </h2>
            <p className="text-white/70 text-lg mt-6 max-w-md leading-relaxed">
              Membership is open to all law-abiding citizens ready and willing to serve.
              Submit your application and a chapter coordinator will reach out.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mt-8">
              <Link
                to="/join"
                data-testid="cta-join-button"
                className="bg-white text-[#0A1128] px-8 py-4 uppercase tracking-[0.14em] text-sm font-bold hover:bg-[#F4A261] transition-colors duration-200 active:scale-95 text-center"
              >
                Join Our Newsletter
              </Link>
              <a
                href={DONATE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-donate-button"
                className="btn-glow bg-[#D90429] text-white px-8 py-4 uppercase tracking-[0.14em] text-sm font-bold hover:bg-[#EF233C] transition-colors duration-200 text-center"
              >
                Fund The Mission
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CHAPTER MAP + FINDER */}
      <ChapterSection />

      {/* NEWSLETTER */}
      <section className="bg-[#0A1128] text-white py-24 grain stars relative">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] glow-gold pointer-events-none" />
        <div className="max-w-3xl mx-auto px-5 sm:px-8 relative z-10">
          <div className="bg-[#111936]/80 backdrop-blur-xl border border-[#F4A261]/30 p-10 sm:p-14 text-center notch">
            <span className="kicker-pill mx-auto">Stay Informed</span>
            <h2 className="font-display text-4xl sm:text-5xl uppercase leading-[0.92] mt-5">
              Join The <span className="text-[#D90429]">Movement</span>
            </h2>
            <p className="text-white/70 text-lg mt-5 max-w-xl mx-auto leading-relaxed">
              Sign up for our newsletter and be the first to hear about rallies, trainings,
              and how you can stand with us.
            </p>
            <NewsletterSignup variant="section" />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
